/**
 * Detect Maater / FlowNotes app URLs for command-palette paste.
 * Documents use extractMaaterDocumentUrlInfo; inbox/chat/settings use extractMaaterAppRoute.
 * Sibling to Basecamp URL detection in command-palette.utils.
 */
import { resolveDocumentDeepLinkFromPathname } from '@/lib/document-deep-link-cold-load'
import { parseAppPath, type UrlIntent, type WorkspaceRouteInfo } from '@/lib/app-routes'

/** Hosts that serve the Maater webapp (not arbitrary sites). */
const MAATER_APP_HOSTS = new Set([
  'flownotes.space',
  'www.flownotes.space',
  'flownotes.localhost',
  'app.maater.localhost',
  'localhost',
  '127.0.0.1',
  'maater.ai',
  'www.maater.ai',
  'app.maater.ai',
  'maater.com',
  'www.maater.com',
  'app.maater.com',
])

export type MaaterAppUrlParts = {
  pathname: string
  /** Search string including leading `?`, or empty. */
  search: string
  host: string | null
}

export type MaaterDocumentUrlInfo = {
  /** Pathname used for parse (leading slash) */
  pathname: string
  /** Extensionless path or UUID for open / by-path */
  pathOrId: string
  /** Hostname when absolute URL; null for relative `/...` paste */
  host: string | null
}

export type MaaterAppRouteInfo = MaaterAppUrlParts & {
  intent: UrlIntent
}

function isMaaterAppHost(hostname: string): boolean {
  const host = hostname.trim().toLowerCase()
  if (MAATER_APP_HOSTS.has(host)) return true
  // Local Vite / proxy: *.localhost
  if (host.endsWith('.localhost')) return true
  return false
}

/** Host + pathname from a pasted Maater app URL (document, inbox thread, chat, …). */
export function parseMaaterAppUrlParts(input: string): MaaterAppUrlParts | null {
  const raw = input.trim()
  if (!raw) return null

  let pathname: string
  let search = ''
  let host: string | null = null

  const noHash = raw.split('#')[0] || '/'
  const stripped = noHash.split('?')[0] || '/'

  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    let url: URL
    try {
      url = new URL(noHash)
    } catch {
      return null
    }
    if (!isMaaterAppHost(url.hostname)) return null
    host = url.hostname.toLowerCase()
    pathname = url.pathname || '/'
    search = url.search || ''
  } else if (stripped.startsWith('/')) {
    try {
      const url = new URL(noHash, 'https://app.maater.ai')
      pathname = url.pathname || '/'
      search = url.search || ''
    } catch {
      pathname = stripped || '/'
    }
  } else {
    const docUuid = stripped.match(
      /^doc\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i,
    )
    if (docUuid) {
      pathname = `/doc/${docUuid[1]}`
    } else {
      const hostPath = noHash.match(/^([A-Za-z0-9.-]+)(\/.+)$/)
      if (hostPath && isMaaterAppHost(hostPath[1])) {
        host = hostPath[1].toLowerCase()
        try {
          const url = new URL(hostPath[2], 'https://app.maater.ai')
          pathname = url.pathname || hostPath[2].split('?')[0]
          search = url.search || ''
        } catch {
          pathname = hostPath[2].split('?')[0]
        }
      } else {
        return null
      }
    }
  }

  return { pathname, search, host }
}

/**
 * Any first-class in-app route (inbox thread, chat, settings, document, …).
 * Home and unknown stay null so we do not steal generic Open URL.
 */
export function extractMaaterAppRoute(
  input: string,
  workspaces: WorkspaceRouteInfo[] = [],
): MaaterAppRouteInfo | null {
  const parts = parseMaaterAppUrlParts(input)
  if (!parts) return null
  let intent = parseAppPath(parts.pathname, new URLSearchParams(), workspaces)
  if (intent.type === 'legacy-redirect') {
    intent = parseAppPath(intent.targetPath, new URLSearchParams(), workspaces)
  }
  if (intent.type === 'unknown' || intent.type === 'home') return null
  return { ...parts, intent }
}

export function isMaaterAppRouteQuery(
  input: string,
  workspaces: WorkspaceRouteInfo[] = [],
): boolean {
  return extractMaaterAppRoute(input, workspaces) != null
}

/**
 * Extract a Maater document path from a pasted absolute or relative app URL.
 * Returns null for Basecamp, external sites, home, or non-document routes.
 */
export function extractMaaterDocumentUrlInfo(input: string): MaaterDocumentUrlInfo | null {
  const parts = parseMaaterAppUrlParts(input)
  if (!parts) return null

  const resolved = resolveDocumentDeepLinkFromPathname(parts.pathname)
  if (!resolved) return null

  return {
    pathname: resolved.pathname,
    pathOrId: resolved.pathOrId,
    host: parts.host,
  }
}

export function isMaaterDocumentUrlQuery(input: string): boolean {
  return extractMaaterDocumentUrlInfo(input) != null
}

/**
 * Href to pass to in-app document navigation (tab system / resolve-link).
 * Returns null for true external links (open in a new browser tab).
 *
 * Relative markdown paths stay as-is. Absolute Maater document URLs
 * (`https://app.maater.ai/doc/{uuid}`) and chat links (`doc:{uuid}`) are
 * rewritten to pathname so clicks stay in the current Maater tab.
 * Cmd-click / pinned-current follow ADR-0016.
 */
/**
 * Absolute local-dev URL (`app.maater.localhost`, `localhost`).
 * These must stay browser links. Rewriting them to `/doc/{uuid}` makes the
 * click resolve against whichever database the current tab is using, and a
 * Docker-only fixture disappears.
 */
export function absoluteLocalDevHref(href: string): string | null {
  const raw = href.trim()
  if (!/^https?:\/\//i.test(raw)) return null
  let url: URL
  try {
    url = new URL(raw)
  } catch {
    return null
  }
  const host = url.hostname.toLowerCase()
  const local = host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost')
  if (!local) return null
  return url.href
}

export function hrefForInAppDocumentNavigation(href: string): string | null {
  const raw = href.trim()
  if (!raw) return null
  if (/^(mailto:|javascript:|tel:)/i.test(raw)) return null

  const docProtocol = raw.match(/^doc:([0-9a-f-]{36})$/i)
  if (docProtocol) return `/doc/${docProtocol[1]}`

  const info = extractMaaterDocumentUrlInfo(raw)
  if (info) return info.pathname

  if (/^https?:\/\//i.test(raw) || raw.startsWith('//')) return null

  return raw
}

/**
 * True when href is a first-class Maater app route that is not a document
 * (`/inbox`, `/{slug}/basecamp/todos`, settings, chat, …).
 */
export function isMaaterNonDocumentAppHref(href: string): boolean {
  const raw = href.trim()
  if (!raw) return false
  return extractMaaterAppRoute(raw) != null && extractMaaterDocumentUrlInfo(raw) == null
}

/**
 * Href to pass to React Router / url-sync (documents + other Maater app routes).
 * Absolute `https://app.maater.ai/...` share links become pathname + search so
 * Inbox / markdown clicks stay in the current Chrome tab (ADR-0016).
 */
export function hrefForInAppNavigation(href: string): string | null {
  const raw = href.trim()
  if (!raw) return null
  if (/^(mailto:|javascript:|tel:)/i.test(raw)) return null

  const doc = extractMaaterDocumentUrlInfo(raw)
  if (doc) return doc.pathname

  const route = extractMaaterAppRoute(raw)
  if (route) {
    return route.search ? `${route.pathname}${route.search}` : route.pathname
  }

  if (/^https?:\/\//i.test(raw) || raw.startsWith('//')) return null

  return raw
}
